import { useRootFiIntegrations } from '@commenda/api/rootFi';
import { Button, Icons } from '@commenda/kakashi';
import { TRootfiSyncStepDefinition } from '@commenda/types';
import { rootFiConnectionServesUseCase } from '@commenda/utils';
import { RootfiCompanyUseCase, TaskStatus } from '@prisma/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { RootFiInviteLinkConnectModal } from '../../../../root-fi';
import { useRootFiIntegration } from '../../../../root-fi/hooks/useRootfiIntegration';
import { getRootfiIntegrationNameFromIntegration } from '../../../../sales-tax/common/integrationDataSync/utils';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';

const DEFAULT_HEADING = 'Connect an integration to continue';
const DEFAULT_DESCRIPTION =
  'Choose an integration to connect. It takes a few seconds, and you can disconnect anytime.';
const RECONNECT_DESCRIPTION =
  "We need a few extra permissions from your integration. Reconnecting takes a few seconds and won't affect your data.";

type Integration = ReturnType<typeof useRootFiIntegrations>['data'][number];

// CONNECTED-first; falls back to most-recent-by-id. Pre-existing data can
// leave a healthy CONNECTED row alongside a higher-id orphan PENDING row.
function pickActiveIntegration(integrations: Integration[] | undefined, useCase: RootfiCompanyUseCase | undefined): Integration | undefined {
  const candidates = integrations?.filter((i) =>
    useCase !== undefined && rootFiConnectionServesUseCase(i, useCase)
  ) ?? [];
  const byIdDesc = (a: Integration, b: Integration) => b.id - a.id;
  return (
    candidates.filter((i) => i.connectionStatus === 'CONNECTED').sort(byIdDesc)[0] ??
    candidates.sort(byIdDesc)[0]
  );
}

export const WorkflowRootfiSyncTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, mutate, workflowExecution, serviceRequest } = context;
  const { setNotification } = useNotification();

  const [connectModalOpen, setConnectModalOpen] = useState(false);

  const stepDefinition = useMemo(() => {
    if (!workflowExecution?.WorkflowTemplateVersion?.definition) return null;
    return workflowExecution.WorkflowTemplateVersion.definition.steps.find(
      (step) => step.id === task.workflowDefinitionStepId
    ) as TRootfiSyncStepDefinition | undefined;
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const corporationId = serviceRequest?.corporation?.id;
  const stepUseCase = stepDefinition?.rootfi?.useCase;
  const safeCorpId = corporationId && stepUseCase ? corporationId : 0;

  const { data: integrations, mutate: refetchIntegrations } = useRootFiIntegrations(
    safeCorpId,
    stepUseCase ?? RootfiCompanyUseCase.SALES_TAX
  );

  const matchingIntegration = useMemo(
    () => pickActiveIntegration(integrations, stepUseCase),
    [integrations, stepUseCase]
  );

  const completeOn = stepDefinition?.rootfi?.completeOn ?? 'connection_established';
  const hasMissingScopes = (matchingIntegration?.scopeData ?? []).some((s) => s.scope_missing.length > 0);
  const isDisconnected = matchingIntegration?.connectionStatus === 'DISCONNECTED';
  const hasPriorConsent =
    !!matchingIntegration && matchingIntegration.connectionStatus !== 'PENDING';
  const needsReconnect = isDisconnected || (hasPriorConsent && hasMissingScopes);

  const waitingOnSync =
    completeOn === 'sync_complete' &&
    matchingIntegration?.connectionStatus === 'CONNECTED' &&
    matchingIntegration?.syncEnabled === true &&
    matchingIntegration?.syncStatus === 'IN_PROGRESS' &&
    !hasMissingScopes;

  // Cross-tab convergence — webhook-driven completion can land while the user
  // is on this tab; poll so the UI catches up without a manual refresh.
  useEffect(() => {
    const taskIsActive =
      task.status !== TaskStatus.COMPLETED &&
      task.status !== TaskStatus.BACKLOG;
    if (!taskIsActive || !corporationId) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      mutate();
       refetchIntegrations();
    }, 5000);
    return () => clearInterval(interval);
  }, [task.status, corporationId, mutate, refetchIntegrations]);

  // Only the assigned CLIENT can act — admin/agent see a passive label.
  const canActOnTask = role === task.pendingOn;

  const { openRootfiLink } = useRootFiIntegration({
    corporationId: safeCorpId,
    onSuccess: () => {
      mutate();
    },
    refetchIntegrations: () => {
      refetchIntegrations();
    },
    onError: (message) => {
      setNotification({ show: true, valid: false, title: 'Connection failed', message });
    },
  });

  // Connect: open the modal so the user picks an integration; the invite link
  // is minted (and bound to this task) on submit. Reconnect reuses the
  // existing connection's invite link directly.
  const handleConnectClick = useCallback(() => {
    if (needsReconnect && matchingIntegration?.inviteLinkId) {
      openRootfiLink(matchingIntegration.inviteLinkId);
      return;
    }
    setConnectModalOpen(true);
  }, [needsReconnect, matchingIntegration?.inviteLinkId, openRootfiLink]);

  const onInviteLinkSaved = useCallback(
    (savedInviteLinkId: string) => {
      refetchIntegrations();
      openRootfiLink(savedInviteLinkId);
      setConnectModalOpen(false);
    },
    [refetchIntegrations, openRootfiLink]
  );

  if (!workflowExecution || !stepDefinition || !corporationId) {
    return (
      <div className="p-4 text-center">
        <p className="text-text-error txt-small-regular">
          Failed to load workflow step information
        </p>
      </div>
    );
  }

  const integrationName = matchingIntegration?.integration
    ? getRootfiIntegrationNameFromIntegration(matchingIntegration.integration)
    : null;

  const heading =
    needsReconnect && integrationName
      ? `Reconnect ${integrationName} to continue`
      : DEFAULT_HEADING;

  const descriptionText = needsReconnect ? RECONNECT_DESCRIPTION : DEFAULT_DESCRIPTION;
  const ctaLabel = needsReconnect ? 'Reconnect integration' : 'Connect integration';
  const passiveLabel = needsReconnect
    ? 'Customer needs to reconnect their integration.'
    : 'Customer needs to connect their integration.';

  const cta = waitingOnSync ? (
    <div className="flex flex-col gap-1">
      <Button size="sm" variant="primary" disabled aria-busy className="self-start">
        <Icons.Loading01 className="size-4 animate-spin" />
        Syncing…
      </Button>
      <span className="txt-extra-small-regular text-text-tertiary">
        This can take a while. Feel free to leave the page — we'll continue
        syncing your data in the background.
      </span>
    </div>
  ) : !canActOnTask ? (
    <div className="flex items-center gap-2 txt-small text-fg-secondary">
      <Icons.User01 className="size-4" />
      {passiveLabel}
    </div>
  ) : (
    <div className="flex flex-col gap-1">
      <Button
        size="sm"
        variant="primary"
        onClick={handleConnectClick}
        className="self-start"
      >
        <Icons.Link01 className="w-4" />
        {ctaLabel}
      </Button>
      <RootFiInviteLinkConnectModal
        corporationId={safeCorpId}
        useCases={[stepUseCase ?? RootfiCompanyUseCase.SALES_TAX]}
        isOpen={connectModalOpen}
        serviceRequestTaskId={task.id}
        onClose={() => setConnectModalOpen(false)}
        onInviteLinkSaved={onInviteLinkSaved}
      />
    </div>
  );

  const description = (
    <p className="whitespace-pre-line txt-extra-small-regular text-text-secondary">
      {descriptionText}
    </p>
  );

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: () => ({
      label: <ServiceRequestTaskLabel label={heading} />,
      title: heading,
      description,
      actions: task.status === TaskStatus.COMPLETED ? null : cta,
    }),
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{ role, status: task.status }}
      config={config}
      context={context}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
